/* A DocType's list — as a tree where it is one.
 *
 * Fourteen in-scope DocTypes are hierarchies, and the decision of which view
 * to open is the DocType's, not a person's: nobody opens Chart of Accounts
 * hoping for 126 alphabetical rows. The flat list stays one click away,
 * because searching and filtering still belong to it.
 */
import { useState } from 'react'
import { ListView, TreeView } from '@jackdesk/erp-kit'
import index from './doctypes.generated.json'
import { labelFor } from './nav'

/* Which DocTypes are hierarchies is a fact about the DocType, not about the
 * person or the tenant, and it is already in the bundle. Asking the server
 * for it held the whole list behind a round trip that could only ever give
 * one of fourteen names — 180ms of nothing on a mid-range phone, and the
 * single largest item in the list's first-paint budget. */
const TREES = new Set(index.filter((d) => d.t).map((d) => d.n))

/** What reads well beside a node in each hierarchy. */
const TREE_EXTRAS: Record<string, string[]> = {
  Account: ['root_type', 'account_type'],
  'Cost Center': [],
  Warehouse: ['warehouse_type'],
  Location: [],
}

export function ListScreen({ doctype, onOpen, onNew, onImport, readOnly }: {
  doctype: string
  onOpen: (name: string) => void
  onNew: () => void
  onImport: () => void
  /** A lapsed subscription: everything is readable, nothing can be made. */
  readOnly?: boolean
}) {
  const isTree = TREES.has(doctype)
  const [asTree, setAsTree] = useState(isTree)

  const title = labelFor(doctype)
  const toggle = isTree ? (
    <button className="btn btn-sm" onClick={() => setAsTree((v) => !v)}>
      {asTree ? 'Show as a list' : 'Show as a tree'}
    </button>
  ) : null

  if (asTree) {
    return (
      <TreeView doctype={doctype} title={title} onOpen={onOpen} actions={toggle}
                onNew={readOnly ? undefined : onNew}
                extras={TREE_EXTRAS[doctype] ?? []} />
    )
  }

  return <ListView doctype={doctype} title={title} onOpen={onOpen}
                   onNew={readOnly ? undefined : onNew}
                   onImport={readOnly ? undefined : onImport} actions={toggle} />
}
